import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';

import { ApplicationState } from '../../../store';
import { services } from '../../../services';
import { useBackgroundPromiseEffectByCondition } from '../../../helpers/hooks/useBackgroundPromiseEffectByCondition';
import { PageHeading } from '../../../components/synced/ui/PageHeading/PageHeading';
import { EmptyState } from '../../../components/synced/ui/EmptyState/EmptyState';

import styles from './MyUsersList.module.css';
import { UserAvatar } from '../../../components/synced/ui/UserAvatar/UserAvatar';
import { ChildContractorUser } from '../models/ChildContractorUser';

export const MyUsersList: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const userContext = useSelector((appState: ApplicationState) => appState.context.userContext);
    const [users, setUsers] = React.useState<ChildContractorUser[]>([]);
    const isChildAccount = userContext.OwnerId != null;

    React.useEffect(() => {
        if (isChildAccount) {
            navigate('/unauthorized');
        }
    }, [isChildAccount, navigate]);

    useBackgroundPromiseEffectByCondition(
        () => !isChildAccount,
        () => services.userService.getChildUsers(),
        setUsers,
        [isChildAccount, location.key]
    );

    const total = users.length;
    const subtitle =
        total === 0
            ? 'Ни одного пользователя пока не добавлено'
            : total === 1
              ? '1 пользователь с доступом к порталу'
              : `${total} пользователей с доступом к порталу`;

    return (
        <div className={styles.root}>
            <div className={styles.headingRow}>
                <PageHeading icon="fa-users" title="Мои пользователи" subtitle={subtitle} />
                <button type="button" className={styles.btnCreate} onClick={() => navigate('/my-users/create')}>
                    <i className="fa-solid fa-plus" style={{ fontSize: 13 }} />
                    Создать
                </button>
            </div>

            <div className={styles.card}>
                {total > 0 && (
                    <div className={styles.toolbar}>
                        <span className={styles.toolbarCount}>Всего: {total}</span>
                    </div>
                )}

                <table className={styles.desktopTable}>
                    <thead>
                        <tr className={styles.desktopHeader}>
                            <th style={{ width: '48%' }}>Пользователь</th>
                            <th style={{ width: '42%' }}>Email</th>
                            <th style={{ width: '10%' }} />
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user, idx) => (
                            <tr
                                key={user.Id}
                                className={`${styles.desktopRow} ${user.IsBlocked ? styles.desktopRowBlocked : ''}`}
                                onClick={() => navigate(`/my-users/${user.Id}`)}
                            >
                                <td className={styles.desktopCell}>
                                    <div className={styles.cellUser}>
                                        <UserAvatar name={user.Title || user.Login || ''} size="md" index={idx} />
                                        <span className={styles.userName}>{user.Title}</span>
                                    </div>
                                </td>
                                <td className={styles.desktopCell}>
                                    <span className={styles.userEmail}>
                                        <i className={`fa-regular fa-envelope ${styles.emailIcon}`} />
                                        {user.Email || user.Login}
                                    </span>
                                </td>
                                <td className={styles.desktopCell}>
                                    <div className={styles.actions}>
                                        <button
                                            type="button"
                                            className={styles.btnEdit}
                                            title="Редактировать"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/my-users/${user.Id}`);
                                            }}
                                        >
                                            <i className="fa-regular fa-pen-to-square" />
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.btnEdit}
                                            title={user.IsBlocked ? 'Разблокировать' : 'Заблокировать'}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                services.userService
                                                    .toggleBlockChildUser(user.Id)
                                                    .then(() =>
                                                        setUsers((prev) =>
                                                            prev.map((u) =>
                                                                u.Id === user.Id ? { ...u, IsBlocked: !u.IsBlocked } : u
                                                            )
                                                        )
                                                    );
                                            }}
                                        >
                                            <i
                                                className={
                                                    !user.IsBlocked ? 'fa-solid fa-lock-open' : 'fa-solid fa-lock'
                                                }
                                            />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className={styles.mobileList}>
                    {users.map((user, idx) => (
                        <div
                            key={user.Id}
                            className={styles.mobileRow}
                            onClick={() => navigate(`/my-users/${user.Id}`)}
                        >
                            <UserAvatar name={user.Title || user.Login || ''} size="lg" index={idx} />
                            <div className={styles.mobileInfo}>
                                <div className={styles.mobileName}>{user.Title}</div>
                                <div className={styles.mobileEmail}>{user.Email || user.Login}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {total === 0 && (
                    <EmptyState
                        icon="fa-user-plus"
                        title="Пользователи ещё не добавлены"
                        text="Добавьте коллег, чтобы они получили доступ к документации и отчётам вашей организации."
                        action={{ label: 'Создать пользователя', onClick: () => navigate('/my-users/create') }}
                    />
                )}
            </div>
        </div>
    );
};
